import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';

import { useConfirmationDialog, type ConfirmationActionProps } from '../model';
import { Trigger } from './trigger';

export const ConfirmationAction = ({
  trigger,
  triggerText,
  triggerVariant = 'default',
  triggerClassName,
  triggerDisabled = false,

  title,
  description,
  confirmText,
  cancelText,
  confirmVariant = 'default',

  onConfirm,
  onCancel,

  isLoading = false,
  loadingText
}: ConfirmationActionProps) => {
  const { t } = useTranslation();
  const { isOpen, isConfirming, open, close, setConfirming } = useConfirmationDialog();

  const handleConfirm = async () => {
    try {
      setConfirming(true);
      await onConfirm();
      close();
    } catch (error) {
      console.error('Confirmation action failed:', error);
    } finally {
      setConfirming(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    close();
  };

  const actualIsLoading = isLoading || isConfirming;

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => (open ? undefined : close())}>
      <AlertDialogTrigger asChild onClick={open}>
        <Trigger
          trigger={trigger}
          triggerText={triggerText}
          triggerVariant={triggerVariant}
          triggerClassName={triggerClassName}
          triggerDisabled={triggerDisabled}
          isLoading={actualIsLoading}
          loadingText={loadingText}
        />
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title || t('common.confirmAction')}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel} disabled={actualIsLoading}>
            {cancelText || t('common.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={actualIsLoading}
            className={
              confirmVariant === 'destructive'
                ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : ''
            }>
            {actualIsLoading ? loadingText || t('common.loading') : confirmText || t('common.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
